import MarketingLayout from '../../components/marketing/MarketingLayout';
import { Card, Title, Text } from "@tremor/react";
import { useState } from 'react';
import BookingWidget from '../../components/BookingWidget';
import ServicePageNavigation from '../../components/ServicePageNavigation';
import { colors } from '../../lib/colors';

export default function GiantTfsaTaxExemptPolicies() {
  const [showBookingWidget, setShowBookingWidget] = useState(false);

  return (
    <MarketingLayout>
      <section className="py-16 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto relative">
          {/* Service Navigation */}
          {/* Corrected slug to match slugify("Corporate Tax-Exempt Insurance Strategy") */}
          <ServicePageNavigation currentSlug="corporate-tax-exempt-insurance-strategy" />
          {/* Header */}
          <div className="text-center mb-12">
            <Title className="text-4xl font-bold text-gray-800 dark:text-white mb-4">Corporate Tax-Exempt Insurance Strategy</Title>
            <Text className="text-xl text-gray-600 dark:text-gray-400">
              Leveraging Life Insurance for Tax-Sheltered Growth Within Your Corporation (Often Called "Giant TFSA")
            </Text>
          </div>

          <div className="space-y-8">
            {/* What is It? */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">What is the Corporate Tax-Exempt Strategy?</Title>
              <Text className="text-gray-600 dark:text-gray-300">
                This strategy involves a corporation purchasing a permanent life insurance policy (like Whole Life or Universal Life) on the life of a shareholder or key employee. While often informally compared to a "Giant TFSA" due to its tax-sheltering capabilities, it is fundamentally a <strong>corporate-owned life insurance policy</strong>.
              </Text>
              <Text className="text-gray-600 dark:text-gray-300 mt-2">
                The primary goal is to accumulate wealth within the policy on a tax-deferred basis. The cash value inside the policy grows sheltered from annual taxation, and upon the death of the insured, the policy pays out a death benefit to the corporation, a significant portion of which can often be flowed out tax-free to shareholders via the Capital Dividend Account (CDA).
              </Text>
            </Card>

            {/* How It Works */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">How the Strategy Works</Title>
              <ol className="list-decimal list-inside text-gray-600 dark:text-gray-300 space-y-2 mt-4">
                <li>
                  <strong>Policy Purchase:</strong> The corporation purchases a permanent life insurance policy on a key individual (usually a shareholder). The corporation is the owner and beneficiary of the policy.
                </li>
                <li>
                  <strong>Premium Payments:</strong> The corporation uses corporate funds (often retained earnings that would otherwise be subject to investment taxes) to pay the policy premiums.
                </li>
                <li>
                  <strong>Tax-Sheltered Growth:</strong> The cash surrender value (CSV) within the policy grows over time, sheltered from annual corporate investment taxes (passive income rules may still apply depending on structure and asset levels).
                </li>
                <li>
                  <strong>Accessing Value (Optional):</strong> The corporation may be able to access the policy's cash value during the insured's lifetime through policy loans or withdrawals, though this can have tax implications and reduce the death benefit. Collateral loans using the policy as security are another option.
                </li>
                <li>
                  <strong>Death Benefit Payout:</strong> Upon the insured person's death, the insurance company pays the death benefit to the corporation.
                </li>
                <li>
                  <strong>Capital Dividend Account (CDA):</strong> A portion of the death benefit (generally the total benefit minus the policy's Adjusted Cost Basis or ACB) creates a credit in the corporation's CDA. This allows the corporation to pay tax-free capital dividends to its shareholders.
                </li>
              </ol>
            </Card>

            {/* Key Benefits */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Key Benefits for Corporations</Title>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Tax-Sheltered Accumulation:</strong> Investment growth within the policy's cash value is not taxed annually like typical passive corporate investments.</li>
                <li><strong>Tax-Free Wealth Transfer:</strong> The CDA credit allows significant wealth extraction from the corporation to shareholders on a tax-free basis upon death.</li>
                <li><strong>Enhanced Estate Value:</strong> Provides liquidity to the corporation upon death, which can then be distributed to the estate/shareholders efficiently.</li>
                <li><strong>Potential Creditor Protection:</strong> Depending on the structure and jurisdiction, the cash value within a life insurance policy may offer protection from corporate creditors.</li>
                <li><strong>Alternative Asset Class:</strong> Diversifies corporate investments into a stable, tax-preferred asset.</li>
                <li><strong>Flexibility:</strong> Can potentially be used as collateral for corporate loans.</li>
              </ul>
            </Card>

            {/* Considerations */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Important Considerations</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">Key factors to evaluate:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Not a TFSA:</strong> It's crucial to understand this is life insurance, not a government TFSA program. The rules, benefits, and access methods differ significantly.</li>
                <li><strong>Long-Term Strategy:</strong> This is designed for long-term accumulation; early surrender often results in losses.</li>
                <li><strong>Liquidity:</strong> Accessing cash value before death can have costs, tax implications, and reduce the final death benefit. It's less liquid than traditional investments.</li>
                <li><strong>Premium Costs:</strong> Requires consistent funding over many years.</li>
                <li><strong>Complexity:</strong> Involves insurance contracts, tax rules (ACB, CDA), and requires careful planning and professional advice.</li>
                 <li><strong>Passive Income Rules:</strong> While growth is sheltered, significant passive assets (including policy cash values) within a corporation can impact access to the Small Business Deduction.</li>
                <li><strong>Underwriting:</strong> The insured individual must qualify for life insurance.</li>
              </ul>
            </Card>

            {/* Suitability */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Is This Strategy Suitable for Your Corporation?</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">This strategy is most often beneficial for:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li>Profitable private corporations (CCPCs) with significant retained earnings subject to passive investment income rules.</li>
                <li>Business owners seeking tax-efficient ways to build and transfer wealth to the next generation or their estate.</li>
                <li>Corporations looking for alternative, stable, long-term investment vehicles.</li>
                <li>Situations where estate liquidity and equalization among heirs are priorities.</li>
                <li>Business owners who have already maximized their personal registered savings plans (RRSP, TFSA).</li>
              </ul>
            </Card>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-4">Unlock Tax-Efficient Corporate Growth</Title>
              <Text className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
                Explore how corporate-owned tax-exempt life insurance can become a powerful tool for accumulating wealth and enhancing estate value within your business structure.
              </Text>
              <button
                onClick={() => setShowBookingWidget(true)}
                className="px-8 py-3 rounded-md text-lg font-semibold text-white transition-transform duration-150 ease-in-out hover:scale-105 active:scale-95"
                style={{ background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` }}
              >
                Discuss Corporate Insurance Strategies
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Widget Modal */}
      {showBookingWidget && (
        <BookingWidget onClose={() => setShowBookingWidget(false)} />
      )}
    </MarketingLayout>
  );
}